// One session channel's live data (Brief 4) — the SHARED session-channel hook,
// consumed by both the desktop mini-app (P6) and the web Dashboard's session
// view (P8). It lives in @waytide/ui so neither surface reaches into the other's
// internals (Blind Modules): both depend on this one contract.
//
// A channel IS one agent_session thread, viewed as mini-Slack: the CONVERSATION
// (message stream) and the ACTIVITY (trace stream) are kept structurally
// distinct — two regions, never one merged log (design-direction §2). Reuses the
// shared transport verbatim (subscribeChat SSE + the REST reads/writes) — no new
// plumbing, no mock data. Steer is the existing optimistic brake: pause/resume
// returns an immediate ack; the agent's true state arrives over presence.
//
// The data client is INJECTED via `useApi()` — the hook no longer
// imports a platform singleton, so web and desktop share it verbatim.

import { useCallback, useEffect, useState } from "react";
import type {
  ChatMessage,
  StreamHealth,
  TraceEvent,
  TraceStreamEvent,
} from "@waytide/core";
import { useApi } from "../api";
import { useOutbox } from "./useOutbox";
import type { OutboxEntry } from "../model/outbox";

export interface ChannelState {
  messages: ChatMessage[];
  /** The activity feed (trace), newest last — kept separate from `messages`. */
  activity: TraceEvent[];
  /** Live connection health (Brief 5 / pt10 + pt20 — stream honesty). The UI
   * reads this to STAY HONEST: only `live` may render as calm; the others dim
   * the stream region + show a quiet, non-alarm cue so a dead feed never looks
   * identical to a quiet-but-live one. */
  health: StreamHealth;
  /** Epoch-ms of the last live frame received on THIS channel (message or
   * activity), or null if none yet. Lets the header say "last update Xm ago"
   * when the stream is lost, so staleness is concrete, not vague. */
  lastEventAt: number | null;
  /** Queue + send a message. Persisted before the wire is touched (Brief 5 /
   * point 15) — never lost on a drop, reload, or crash; cleared only on
   * confirmed success. */
  send: (body: string) => Promise<void>;
  /** This thread's pending (unsent / retrying) messages, oldest-first — render
   * them after `messages` so the user sees their words the instant they Send. */
  pending: OutboxEntry[];
  /** Re-arm a parked (permanently-failed) pending send and try it again. */
  retry: (key: string) => void;
  stop: () => Promise<void>;
  resume: () => Promise<void>;
}

/** Map a live `trace` SSE frame to a timeline row (its durable id arrives on the
 * next read; the live frame carries id 0). */
function frameToEvent(f: TraceStreamEvent): TraceEvent {
  return {
    id: f.id,
    kind: f.kind,
    summary: f.summary,
    evidence_ref: f.evidence_ref,
    cost_micros: f.cost_micros,
    created_at: f.created_at,
  };
}

export function useChannel(threadId: number | null): ChannelState {
  const api = useApi();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [activity, setActivity] = useState<TraceEvent[]>([]);
  // Health starts at `connecting` — never assume `live` before the stream has
  // actually connected (honesty: a fresh mount must not read as calm-live).
  const [health, setHealth] = useState<StreamHealth>("connecting");
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);

  useEffect(() => {
    if (threadId == null) {
      setMessages([]);
      setActivity([]);
      setHealth("connecting");
      setLastEventAt(null);
      return;
    }
    let alive = true;
    setMessages([]);
    setActivity([]);
    setHealth("connecting");
    setLastEventAt(null);
    void api
      .listThreadMessages(threadId)
      .then((r) => alive && setMessages(r.messages))
      .catch(() => {});
    void api
      .getTrace(String(threadId))
      .then((t) => alive && setActivity(t.events))
      .catch(() => {});

    // Live: append only THIS channel's frames, deduped (the sender also sees
    // its own POST result, so messages can arrive twice). Each live frame on
    // this channel stamps `lastEventAt` so a later "stream lost" message can say
    // exactly how stale the feed is.
    const unsub = api.subscribeChat(
      (m) => {
        if (m.thread_id !== threadId) return;
        if (alive) setLastEventAt(Date.now());
        setMessages((prev) =>
          prev.some((x) => x.id === m.id) ? prev : [...prev, m],
        );
      },
      () => {},
      undefined,
      (f) => {
        if (f.thread_id !== threadId) return;
        if (alive) setLastEventAt(Date.now());
        setActivity((prev) => [...prev, frameToEvent(f)]);
      },
      undefined,
      (b) => {
        if (b.thread_id !== threadId) return;
        // Signal changed the brain (e.g. posted a question) — re-read messages
        // so the operator chat shows it live (its writes are direct reducers).
        void api
          .listThreadMessages(threadId)
          .then((r) => alive && setMessages(r.messages))
          .catch(() => {});
      },
      // onHealth: the connection-honesty signal, lifted out of the SSE closure.
      // The UI dims the stream + shows a calm cue whenever this is not `live`.
      (h) => {
        if (alive) setHealth(h);
      },
    );
    return () => {
      alive = false;
      unsub();
    };
  }, [api, threadId]);

  // The durable send path (Brief 5 / point 15): the outbox persists the message
  // BEFORE the wire is touched and clears it only on confirmed success. On
  // success it hands back the server-stamped row, which we merge de-duped on id
  // (the SSE echo of the same row is deduped the same way), so the conversation
  // updates with no extra read.
  //
  // The outbox is a SINGLE global queue, so a flush this instance triggers may
  // drain (and call back about) another thread's entry. Guard on thread_id so a
  // sibling channel's confirmed send never leaks into THIS conversation.
  const mergeSent = useCallback(
    (sent: ChatMessage) => {
      if (sent.thread_id !== threadId) return;
      setMessages((prev) =>
        prev.some((x) => x.id === sent.id) ? prev : [...prev, sent],
      );
    },
    [threadId],
  );
  const { pending, send, retry } = useOutbox(threadId, mergeSent);

  const stop = useCallback(async () => {
    if (threadId != null) await api.pauseSession(threadId);
  }, [api, threadId]);

  const resume = useCallback(async () => {
    if (threadId != null) await api.resumeSession(threadId);
  }, [api, threadId]);

  return {
    messages,
    activity,
    health,
    lastEventAt,
    send,
    pending,
    retry,
    stop,
    resume,
  };
}
